/**
 * GMA-1623 (CompassCare Web 8/14 — Devices) — page composition.
 *
 * The device-fleet screen: a KPI strip from the pure devicesModel rollup
 * (summarizeFleet) over the shared DataTable (its first consumer — the
 * "cheap DataTable win"), one row per device with type, assigned client, an
 * explicit online/offline status chip, the COMPUTED Client Safety Impact
 * badge (computeClientSafetyImpact over the client's other devices — never
 * hand-set), battery, and a last-seen freshness cell. Selecting a row opens
 * the shared DetailDrawer with the device detail body.
 *
 * Care-domain bar: stale sensor data is never presented as current — every
 * row carries a relative last-seen ("—" when unknown, never blank); offline
 * devices are visually distinct (muted chip, not colour-only). Presentational
 * + fail-safe: `devices` is injected by the route (testable), defaults to [],
 * and null/malformed rows can never break the render. All colors come from
 * the single design-token set.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "../../components/web/DataTable";
import KpiStrip from "../../components/web/KpiStrip";
import SeverityBadge from "../../components/web/SeverityBadge";
import DetailDrawer from "../../components/web/DetailDrawer";
import { summarizeFleet, computeClientSafetyImpact } from "./devicesModel";
import {
  PLACEHOLDER,
  formatMeasurement,
  formatRelativeTime,
} from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

// Computed impact level -> severity token + human label. Impact is the
// model's coverage semantics; the severity token only supplies the colour.
const IMPACT_SEVERITY = Object.freeze({
  low: "normal",
  moderate: "caution",
  high: "atRisk",
  critical: "critical",
});
const IMPACT_LABELS = Object.freeze({
  low: "Low",
  moderate: "Moderate",
  high: "High",
  critical: "Critical",
});

const COLUMNS = Object.freeze([
  { key: "device", label: "Device" },
  { key: "type", label: "Type" },
  { key: "client", label: "Client" },
  { key: "status", label: "Status" },
  { key: "impact", label: "Client safety impact" },
  { key: "battery", label: "Battery", align: "right", mono: true },
  { key: "lastSeen", label: "Last seen", align: "right", mono: true },
]);

// Real device rows only — mirrors the model's own malformed-row guard so the
// table and the KPI rollup can never disagree about what counts as a device.
const validDevices = (devices) =>
  (Array.isArray(devices) ? devices : []).filter(
    (d) => d && typeof d === "object" && !Array.isArray(d),
  );

// Group key for "this client's devices" (the impact computation's context).
// Devices with no client information fall into a shared unassigned bucket.
const clientKey = (d) =>
  d.clientId !== null && d.clientId !== undefined
    ? `id:${d.clientId}`
    : d.clientName
      ? `name:${d.clientName}`
      : "unassigned";

/**
 * Online/offline status chip. Offline is a deliberately distinct muted chip
 * (never colour-only, never a clinical severity); online/at-risk reuse the
 * shared SeverityBadge tokens.
 */
function statusChip(device) {
  if (device.status === "offline") {
    return (
      <span
        data-testid="device-status"
        data-status="offline"
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 999,
          fontSize: 12,
          whiteSpace: "nowrap",
          border: `1px solid ${theme.border}`,
          background: "transparent",
          color: theme.textMuted,
        }}
      >
        Offline
      </span>
    );
  }
  const atRisk = device.status === "atRisk" || device.status === "at_risk";
  return (
    <span data-testid="device-status" data-status={atRisk ? "atRisk" : "online"}>
      <SeverityBadge
        level={atRisk ? "atRisk" : "normal"}
        label={atRisk ? "At risk" : "Online"}
      />
    </span>
  );
}

/** Computed Client Safety Impact badge (colour from the severity tokens). */
function impactBadge(impact) {
  return (
    <span data-testid="device-impact" data-impact={impact}>
      <SeverityBadge
        level={IMPACT_SEVERITY[impact] || "normal"}
        label={IMPACT_LABELS[impact] || IMPACT_LABELS.low}
      />
    </span>
  );
}

// One labelled field in the drawer body.
function DrawerField({ label, children }) {
  return (
    <section aria-label={label}>
      <h3
        style={{
          margin: "0 0 4px",
          fontSize: 13,
          color: theme.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </h3>
      <div style={{ margin: 0, fontSize: 14 }}>{children}</div>
    </section>
  );
}

DrawerField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default function DevicesPage({ devices = [], now }) {
  const [selected, setSelected] = useState(null); // display row | null

  const list = useMemo(() => validDevices(devices), [devices]);

  // Fleet KPI rollup from the pure model (single source with the table rows).
  const summary = useMemo(() => summarizeFleet(list), [list]);

  const kpis = useMemo(
    () => [
      { key: "total", label: "Devices", value: summary.total },
      { key: "online", label: "Online", value: summary.online },
      {
        key: "offline",
        label: "Offline",
        value: summary.offline,
        severity: summary.offline > 0 ? "critical" : undefined,
      },
      {
        key: "needsAttention",
        label: "Needs attention",
        value: summary.needsAttention,
        severity: summary.needsAttention > 0 ? "caution" : undefined,
      },
      {
        key: "lowBattery",
        label: "Low battery",
        value: summary.lowBattery,
        severity: summary.lowBattery > 0 ? "caution" : undefined,
      },
    ],
    [summary],
  );

  const rows = useMemo(() => {
    // Client groups give computeClientSafetyImpact its "other devices" context.
    const groups = new Map();
    for (const d of list) {
      const key = clientKey(d);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    }
    return list.map((d, index) => {
      const impact = computeClientSafetyImpact(d, groups.get(clientKey(d)));
      return {
        // Stable row key: device id when present, else a positional fallback.
        id: d.id !== null && d.id !== undefined ? d.id : `device-${index}`,
        device: d.name || "Unknown device",
        type: d.type || PLACEHOLDER,
        client: d.clientName || PLACEHOLDER,
        status: statusChip(d),
        impact: impactBadge(impact),
        battery: formatMeasurement(d.battery, "%"),
        // Freshness is mandatory: relative last-seen, or an explicit "—" when
        // unknown — a device can never silently present stale data as current.
        lastSeen: formatRelativeTime(d.lastSeenAt, now) || PLACEHOLDER,
        _impact: impact,
        _device: d,
      };
    });
  }, [list, now]);

  return (
    <div
      data-testid="devices-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <KpiStrip kpis={kpis} />

      <DataTable
        ariaLabel="Device fleet"
        columns={COLUMNS}
        rows={rows}
        // Accent only when the computed impact is above "low": a healthy row
        // stays un-accented rather than painting a misleading green stripe.
        getRowSeverity={(row) =>
          row._impact === "low" ? null : IMPACT_SEVERITY[row._impact]
        }
        onRowClick={(row) => setSelected(row)}
        getRowLabel={(row) => `Open device: ${row.device}`}
        emptyMessage="No devices registered"
      />

      <DetailDrawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.device : ""}
        type="device"
      >
        {selected ? (
          <div data-testid="device-detail" style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              {statusChip(selected._device)}
              {impactBadge(selected._impact)}
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: theme.fontMono,
                  fontSize: 12,
                  color: theme.textMuted,
                }}
              >
                {selected.lastSeen}
              </span>
            </div>
            <DrawerField label="Client">{selected.client}</DrawerField>
            <DrawerField label="Type">{selected.type}</DrawerField>
            <DrawerField label="Capabilities">
              {Array.isArray(selected._device.capabilities) &&
              selected._device.capabilities.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {selected._device.capabilities.map((c) => (
                    <li key={String(c)}>{String(c)}</li>
                  ))}
                </ul>
              ) : (
                PLACEHOLDER
              )}
            </DrawerField>
            <DrawerField label="Battery">{selected.battery}</DrawerField>
            <DrawerField label="Signal">
              {formatMeasurement(selected._device.signal, "%")}
            </DrawerField>
            <DrawerField label="Firmware">
              {selected._device.firmwarePending === true
                ? "Update pending"
                : "Up to date"}
            </DrawerField>
          </div>
        ) : null}
      </DetailDrawer>
    </div>
  );
}

DevicesPage.propTypes = {
  /**
   * Device fleet rows; { id, name, type, clientId, clientName, status,
   * battery, signal, firmwarePending, capabilities, gatewayId, lastSeenAt }.
   * Typed as a plain array (not arrayOf(shape)) because the page is
   * intentionally fail-safe for null / malformed rows, and a strict element
   * type would warn for those supported inputs.
   */
  devices: PropTypes.array,
  /** Reference time for the last-seen freshness column. */
  now: PropTypes.number,
};
